import { CommonModule } from '@angular/common';
import { Component, effect, input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, Observable, of, Subject, takeUntil, tap } from 'rxjs';
import { ToastService } from 'src/app/core/toast/toast.service';
import { GenericDD } from 'src/app/model/generic-data';
import { ClientiService } from '../clienti/clienti.service';
import { StepDto, UpdateStep } from '../gestione-step/step-dto';
import { GestioneStepService } from '../gestione-step/gestione-step.service';
import { CreateIndoorMap, IndoorMapDto, UpdateIndoorMap } from './mappa-indoor-dto';
import { MappaIndoorService } from './mappa-indoor.service';

interface IndoorMapModel {
  floor: number | null;
  title: string;
  description: string;
  image: string | null;
}

@Component({
  selector: 'app-mappa-indoor',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule],
  templateUrl: './mappa-indoor.component.html',
  styleUrl: './mappa-indoor.component.scss',
})
export class MappaIndoorComponent implements OnDestroy {
  // id della missione (puo essere null in creazione, finche la missione non e salvata)
  readonly missionId = input<string | null>(null);

  clientOptions: GenericDD[] = [];

  // cliente condiviso tra tutti i piani (clientFk del body)
  clientFk: string | null = null;

  // numero di piani/planimetrie (solo UI: decide quante chiamate fare)
  floorCount = 1;

  // una entry per piano (ogni entry = una chiamata POST /missions/indoor-maps)
  maps: IndoorMapModel[] = [this.emptyModel()];

  // mappe gia presenti sul backend
  existingMaps: IndoorMapDto[] = [];

  // gli step della missione: sono loro ad essere posizionati sulla planimetria
  steps: StepDto[] = [];
  selectedStep: StepDto | null = null;

  // piano selezionato per il posizionamento visivo degli step
  placementFloor: number | null = null;

  private theEnd$ = new Subject<void>();

  constructor(
    private mappaIndoorService: MappaIndoorService,
    private stepService: GestioneStepService,
    private clientiService: ClientiService,
    private toast: ToastService,
  ) {
    this.loadClients();

    effect(() => {
      const mission = this.missionId();
      if (mission) {
        this.loadExisting(mission);
        this.loadSteps(mission);
      }
    });
  }

  ngOnDestroy(): void {
    this.theEnd$.next();
    this.theEnd$.complete();
  }

  private emptyModel(): IndoorMapModel {
    return { floor: null, title: '', description: '', image: null };
  }

  private loadClients(): void {
    this.clientiService
      .getAll()
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (clients) => {
          this.clientOptions = clients.map((c) => ({ id: c.id, codice: c.name }));
        },
        error: () => {
          this.clientOptions = [];
        },
      });
  }

  private loadExisting(missionId: string): void {
    this.mappaIndoorService
      .getAll(missionId)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (maps) => {
          this.existingMaps = maps ?? [];
          if (this.existingMaps.length) {
            this.clientFk = this.existingMaps[0].clientFk;
            this.maps = this.existingMaps
              .slice()
              .sort((a, b) => a.floor - b.floor)
              .map((m) => ({
                floor: m.floor,
                title: m.title,
                description: m.description ?? '',
                image: m.image ?? null,
              }));
            this.floorCount = this.maps.length;
            this.placementFloor = this.existingMaps[0].floor;
          }
        },
        error: () => {
          this.existingMaps = [];
        },
      });
  }

  private loadSteps(missionId: string): void {
    this.stepService
      .getAllByMission(missionId)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (data) => {
          this.steps = data ?? [];
        },
        error: () => {
          this.steps = [];
        },
      });
  }

  /* ========== Gestione numero piani ========== */

  onFloorCountChange(): void {
    let count = Math.floor(Number(this.floorCount));
    if (!Number.isFinite(count) || count < 1) count = 1;
    this.floorCount = count;

    if (count > this.maps.length) {
      while (this.maps.length < count) {
        this.maps.push({ ...this.emptyModel(), floor: this.maps.length });
      }
    } else if (count < this.maps.length) {
      this.maps = this.maps.slice(0, count);
    }
  }

  onPlanimetriaSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.maps[index].image = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  /* ========== API usate dal form della missione ========== */

  validate(): boolean {
    if (!this.clientFk) {
      this.toast.error('Seleziona un cliente per la mappa indoor');
      return false;
    }
    if (!this.maps.length) {
      this.toast.error('Indica almeno un piano');
      return false;
    }

    const floors = new Set<number>();
    for (let i = 0; i < this.maps.length; i++) {
      const m = this.maps[i];
      if (m.floor === null || m.floor === undefined) {
        this.toast.error(`Inserisci il numero di piano (planimetria ${i + 1})`);
        return false;
      }
      if (floors.has(m.floor)) {
        this.toast.error(`Piano ${m.floor} duplicato: ogni planimetria deve avere un piano diverso`);
        return false;
      }
      floors.add(m.floor);
      if (!m.title?.trim()) {
        this.toast.error(`Inserisci il titolo (planimetria ${i + 1})`);
        return false;
      }
      if (!m.image) {
        this.toast.error(`Carica la planimetria (planimetria ${i + 1})`);
        return false;
      }
    }
    return true;
  }

  // crea/aggiorna una mappa per ogni piano + elimina i piani rimossi
  persist(missionId: string): Observable<any> {
    if (!this.validate()) {
      return of(null);
    }

    const clientFk = this.clientFk as string;
    const currentFloors = new Set(this.maps.map((m) => m.floor as number));

    const requests: Observable<any>[] = [];

    // create/update per ogni piano
    for (const m of this.maps) {
      const floor = m.floor as number;
      const image = m.image as string;
      const title = m.title.trim();
      const description = m.description?.trim() || '';

      const existing = this.existingMaps.find(
        (e) => e.floor === floor && e.clientFk === clientFk,
      );

      if (existing) {
        const data: UpdateIndoorMap = { image, title, description };
        requests.push(this.mappaIndoorService.update(missionId, clientFk, floor, data));
      } else {
        const data: CreateIndoorMap = {
          floor,
          missionFk: missionId,
          clientFk,
          image,
          title,
          description,
        };
        requests.push(this.mappaIndoorService.create(data));
      }
    }

    // elimina le mappe esistenti non piu presenti (piano rimosso o cliente cambiato)
    for (const e of this.existingMaps) {
      const stillPresent = e.clientFk === clientFk && currentFloors.has(e.floor);
      if (!stillPresent) {
        requests.push(this.mappaIndoorService.delete(e.missionFk, e.clientFk, e.floor));
      }
    }

    if (!requests.length) return of(null);

    return forkJoin(requests).pipe(
      tap(() => {
        this.loadExisting(missionId);
        this.loadSteps(missionId);
      }),
    );
  }

  // elimina tutte le mappe della missione (flag disattivato)
  remove(missionId: string): Observable<any> {
    if (!this.existingMaps.length) {
      return of(void 0);
    }
    const requests = this.existingMaps.map((e) =>
      this.mappaIndoorService.delete(e.missionFk, e.clientFk, e.floor),
    );
    return forkJoin(requests).pipe(
      tap(() => {
        this.existingMaps = [];
        this.maps = [this.emptyModel()];
        this.floorCount = 1;
      }),
    );
  }

  /* ========== Posizionamento visivo step ========== */

  // mappe salvate con immagine (per il posizionamento)
  get savedMaps(): IndoorMapDto[] {
    return this.existingMaps.filter((m) => !!m.image);
  }

  get placementMap(): IndoorMapDto | null {
    return this.savedMaps.find((m) => m.floor === this.placementFloor) ?? null;
  }

  selectPlacementFloor(floor: number): void {
    this.placementFloor = floor;
    this.selectedStep = null;
  }

  get stepsOnMap(): StepDto[] {
    const map = this.placementMap;
    if (!map) return [];
    return this.steps.filter(
      (s) =>
        s.indoorMapFloor === map.floor &&
        s.indoorMapClientFk === map.clientFk &&
        s.x != null &&
        s.y != null,
    );
  }

  isOnCurrentMap(step: StepDto): boolean {
    return this.stepsOnMap.some((s) => s.id === step.id);
  }

  selectStep(step: StepDto): void {
    this.selectedStep = this.selectedStep?.id === step.id ? null : step;
  }

  onMapClick(event: MouseEvent): void {
    const map = this.placementMap;
    if (!map || !this.selectedStep) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    this.saveStepPosition(this.selectedStep, map, this.clamp(x), this.clamp(y));
  }

  removeFromMap(step: StepDto, event: MouseEvent): void {
    event.stopPropagation();
    const map = this.placementMap;
    if (!map) return;
    this.saveStepPosition(step, map, null, null, true);
  }

  private clamp(value: number): number {
    return Math.min(100, Math.max(0, Math.round(value * 100) / 100));
  }

  private saveStepPosition(
    step: StepDto,
    map: IndoorMapDto,
    x: number | null,
    y: number | null,
    unassign = false,
  ): void {
    const data: UpdateStep = {
      name: step.name,
      shortDescription: step.shortDescription,
      description: step.description,
      missionFk: step.missionFk,
      dependsOn: step.dependsOn,
      x,
      y,
      indoorMapFloor: unassign ? null : map.floor,
      indoorMapClientFk: unassign ? null : map.clientFk,
    };

    this.stepService
      .update(step.id, data)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (updated) => {
          const i = this.steps.findIndex((s) => s.id === step.id);
          if (i > -1) {
            this.steps[i] = { ...this.steps[i], ...updated };
            this.steps = [...this.steps];
          }
          if (unassign) {
            this.toast.success('Step rimosso dalla planimetria');
            if (this.selectedStep?.id === step.id) this.selectedStep = null;
          }
        },
        error: () => {},
      });
  }
}
